import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import { Helmet } from "react-helmet-async";
import PageTitle from "../components/PageTitle";
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Typography,
  Collapse,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AgentClientsExtra from "../components/AgentClientsExtra";
import StyledDrawer from "../components/StyledDrawer";
import apiClient from "../utils/apiClient";
import CloseIcon from "@mui/icons-material/Close";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/ar";
import "dayjs/locale/tr";
import "dayjs/locale/hy-am";

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;

  & > div:not(:last-child) {
    margin-inline-end: 10px;
  }
`;

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 0;
  position: relative;
  overflow: visible;
  gap: 10px;
`;

const AlertTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClientsMonthlyAdjustments = () => {
  const { t, i18n } = useTranslation();

  const dayjsLocaleMap = {
    ru: "ru",
    en: "en",
    ar: "ar",
    tr: "tr",
    hy: "hy-am",
  };

  const currentLanguage = i18n.language?.split("-")[0] || "ru";
  const dayjsLocale = dayjsLocaleMap[currentLanguage] || "ru";

  const [refreshCount, setRefreshCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [info, setInfo] = useState();

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState(null);

  const handleSelectFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const isXlsx =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isXlsx) {
      alert(t("monthlyAdjustments.validation.onlyXlsx"));
      return;
    }

    setSelectedFile(file);
  };

  useEffect(() => {
    async function getInfo() {
      try {
        const response = await apiClient.get(
          "/clients_monthly_adjustments/info",
        );

        setInfo(response?.data);
      } catch (error) {
        console.error("Ошибка загрузки данных", error);
      }
    }

    getInfo();
  }, []);

  const period = useMemo(
    () =>
      info?.period
        ? dayjs(info.period).locale(dayjsLocale).format("MMMM YYYY")
        : "",
    [info?.period, dayjsLocale],
  );
  const editStart = useMemo(
    () => dayjs(info?.editStart).format("DD.MM.YY"),
    [info],
  );
  const editEnd = useMemo(
    () => dayjs(info?.editEnd).format("DD.MM.YY"),
    [info],
  );

  const handleClosePopup = () => {
    setRefreshCount((prev) => prev + 1);
    setShowPopup(false);
    setEditingAgent(null);
  };

  const handleSuccessCreate = () => {
    handleClosePopup();
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !info?.canEdit) return;

    try {
      setUploading(true);
      setUploadNotice(null);

      const formData = new FormData();
      formData.append("file", selectedFile);

      const resp = await apiClient.post(
        "/clients_monthly_adjustments/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const r = resp?.data?.result;
      const hasErrors = Array.isArray(r?.errors) && r.errors.length > 0;

      setUploadNotice({
        severity: hasErrors ? "warning" : "success",
        title: hasErrors
          ? t("monthlyAdjustments.upload.withWarnings")
          : t("monthlyAdjustments.upload.success"),
        text: t("monthlyAdjustments.upload.result", {
          file: r?.file ?? selectedFile.name,
          processed: r?.processed ?? 0,
          inserted: r?.inserted ?? 0,
          updatedText:
            typeof r?.updated === "number"
              ? t("monthlyAdjustments.upload.updated", { count: r.updated })
              : "",
          errorsText: hasErrors
            ? t("monthlyAdjustments.upload.errors", { count: r.errors.length })
            : "",
        }),
      });

      setSelectedFile(null);
      setRefreshCount((prev) => prev + 1);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        t("monthlyAdjustments.upload.failed");

      setUploadNotice({
        severity: "error",
        title: t("monthlyAdjustments.upload.errorTitle"),
        text: msg,
      });

      console.error("Ошибка загрузки файла", error);
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "format",
      headerName: t("monthlyAdjustments.columns.format"),
      flex: 0.5,
      valueFormatter: (value) => {
        switch (value) {
          case "FIZ":
            return t("monthlyAdjustments.legalForm.person");
          case "IP":
            return t("monthlyAdjustments.legalForm.entrepreneur");
          case "UR":
            return t("monthlyAdjustments.legalForm.company");
          default:
            return value;
        }
      },
    },
    {
      field: "fio",
      headerName: t("monthlyAdjustments.columns.fio"),
      flex: 1.5,
    },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "action",
      headerName: "",
      width: 180,
      type: "actions",
      renderCell: (params) => (
        <ButtonsBlock>
          {!params.row.is_o ? (
            <Typography color="textSecondary">
              {t("monthlyAdjustments.closed")}{" "}
              {params.row?.data_end_doc
                ? dayjs(params.row.data_end_doc).format("DD.MM.YYYY")
                : ""}
            </Typography>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                setEditingAgent(params.row);
                setShowPopup(true);
              }}
              disabled={!info?.canEdit}
            >
              {t("monthlyAdjustments.edit")}
            </Button>
          )}
        </ButtonsBlock>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("monthlyAdjustments.metaTitle")}</title>
        <meta
          name="description"
          content={t("monthlyAdjustments.metaDescription")}
        />
      </Helmet>

      <PageTitle>{t("monthlyAdjustments.title")}</PageTitle>

      <Collapse in={!!uploadNotice}>
        {uploadNotice && (
          <Alert
            severity={uploadNotice.severity}
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setUploadNotice(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>{uploadNotice.title}</AlertTitle>
            {uploadNotice.text}
          </Alert>
        )}
      </Collapse>

      <HeadTabMenu>
        <Alert severity={info?.canEdit ? "info" : "warning"}>
          <AlertTitle>
            <AlertTitleRow>
              <span>
                {t("monthlyAdjustments.reportPeriod")}: <b>{period}</b>
              </span>

              {info?.canEdit && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                  >
                    {t("monthlyAdjustments.chooseFile")}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleSelectFile}
                    />
                  </Button>

                  {selectedFile && (
                    <Typography variant="body2">{selectedFile.name}</Typography>
                  )}

                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUploadFile}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? (
                      <CircularProgress size={16} />
                    ) : (
                      t("monthlyAdjustments.uploadButton")
                    )}
                  </Button>
                </>
              )}
            </AlertTitleRow>
          </AlertTitle>

          {t("monthlyAdjustments.editAvailable", {
            from: editStart,
            to: editEnd,
          })}
        </Alert>
      </HeadTabMenu>

      <DataTable
        isShowSearch={true}
        columns={columns}
        path={"/agents"}
        refresh={refreshCount}
      />

      <StyledDrawer
        show={showPopup && editingAgent?.id > 0}
        width="80vw"
        title={t("monthlyAdjustments.agentTitle", {
          fio: editingAgent?.fio,
          tnId: editingAgent?.user_id_tn,
        })}
        onClose={handleClosePopup}
        ContentComponent={AgentClientsExtra}
        contentProps={{
          onClose: handleClosePopup,
          onSuccess: handleSuccessCreate,
          agent: editingAgent,
          info: info,
        }}
      />
    </>
  );
};

export default ClientsMonthlyAdjustments;
